import { PreAnnotateModelTypeEnum } from "@repo/schema";
import type { TFunction } from "i18next";
import {
  Ban,
  CircleAlert,
  CircleCheck,
  Info,
  LucideIcon,
  MessageSquare,
} from "lucide-react";
import {
  PreAnnotateRunOutcome,
  PreAnnotateRunView,
} from "../../types/preAnnotate";
import {
  BANNER_DONE_BODY,
  BANNER_EMPTY_BODY,
  BANNER_EMPTY_TITLE,
  BANNER_ERROR_BODY,
  BANNER_ERROR_TITLE,
} from "../PreAnnotateSettingsDialog/copy";
import {
  CANCELLED_TITLE,
  RESUMED_TITLE,
  VISION_DONE_FEEDBACK_BODY,
} from "./copy";

type LabelT = TFunction<["label", "common"]>;

/** Icon, colour and wording of a settled auto-annotation, for any surface. */
export interface RunDescription {
  icon: LucideIcon;
  /** Text colour class for the icon. */
  tone: string;
  title: string;
  body?: string;
  /** Robopipe Vision's own sentence about the run, shown under the body. */
  summary?: string;
  /** Errors interrupt; everything else is announced politely. */
  role: "status" | "alert";
}

/** "Added 3 polygons" / "Added 1 box": the geometry the run's mode produces. */
const doneTitle = (t: LabelT, count: number, modelType: PreAnnotateModelTypeEnum) =>
  modelType === PreAnnotateModelTypeEnum.DETECTION
    ? t("label:runOutcome.doneTitle.detection", { count })
    : t("label:runOutcome.doneTitle.segmentation", { count });

/**
 * How a finished run reads. Shared by the run panel above the labels bar and
 * the banner in the settings dialog so the two never disagree.
 */
export const describeRunOutcome = (
  outcome: PreAnnotateRunOutcome,
  t: LabelT,
): RunDescription => {
  switch (outcome.kind) {
    case "done":
      return {
        icon: CircleCheck,
        tone: "text-emerald-700",
        title: doneTitle(t, outcome.count, outcome.modelType),
        // After a feedback turn the interesting fact is that the correction
        // was applied, not the generic "review before saving".
        body:
          (outcome.turn ?? 1) > 1 ? t(VISION_DONE_FEEDBACK_BODY) : t(BANNER_DONE_BODY),
        summary: outcome.summary,
        role: "status",
      };
    case "empty":
      // The model looked and reported nothing. The descriptions are what it
      // looked for, so that is where the fix is.
      return {
        icon: Info,
        tone: "text-blue-600",
        title: t(BANNER_EMPTY_TITLE),
        body: t(BANNER_EMPTY_BODY),
        summary: outcome.summary,
        role: "status",
      };
    case "error":
      return {
        icon: CircleAlert,
        tone: "text-destructive",
        title: t(BANNER_ERROR_TITLE),
        // The API's own reason (a 400 naming what is missing, say) beats the
        // generic connection copy whenever it gave one.
        body: outcome.message ?? t(BANNER_ERROR_BODY),
        role: "alert",
      };
  }
};

/** Every panel view except the live one, which has its own compact card. */
export type SettledRunView = Exclude<PreAnnotateRunView, { kind: "running" }>;

/** How a settled panel view reads (result or cancelled). */
export const describeRunView = (view: SettledRunView, t: LabelT): RunDescription => {
  switch (view.kind) {
    case "outcome":
      return describeRunOutcome(view.outcome, t);
    case "cancelled":
      return {
        icon: Ban,
        tone: "text-muted-foreground",
        title: t(CANCELLED_TITLE),
        role: "status",
      };
    case "resumed":
      return {
        icon: MessageSquare,
        tone: "text-emerald-700",
        title: RESUMED_TITLE,
        body: t("label:runPanel.resumedBody", { count: view.turn }),
        role: "status",
      };
  }
};
